import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from '@app/origami-api/services/base/service.base';
import { MonoTypeOperatorFunction, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SimpleTopic, TopicField } from '@app/origami-api/utils/topic-utils';
import { ErrorCode } from '../constants';

export namespace OrganizationServiceModel {
  export interface OrganizationView {
    EntitiesCount?: number;
    ViewFilterDisplayName: string;
    ViewFilter: string;
    ViewMode: number;
  }

  export interface GetOrganizations {
    siteId: number;
    clientId: number;
    filter: string;
    excludeExisting: boolean;
  }

  export interface Organization extends SimpleTopic {
    TopicId: number;
    OrganizationId: number;
    ClientId: number;
    TopicFields: {
      TopicId: number;
      FieldName: string;
      FieldType: number;
      Fieldvalue: string;
    }[];
  }

  export interface OrganizationResult {
    TotalRowCount: number;
    PageNumber: number;
    Records: Organization[];
  }
}

export namespace OrganizationServiceOptions {
  export interface GetOrganization {
    siteId: number;
    organizationId: number;
  }

  export interface GetOrganizations {
    siteId: number;
    clientId?: number;
    filter?: string;
    excludeExisting?: boolean;
  }

  export interface GetOrganizationViews {
    siteId: number;
    viewMode: number;
    viewFilter?: string;
  }

  export interface GetOrganizationsByView {
    siteId: number;
    viewMode: number;
    viewFilter: string;
    start?: number;
    limit?: number;
    searchQuery?: string;
    orderBy?: string;
    fieldListing?: string;
    organizationId?: number;
  }

  export interface CreateOrganizationBody {
    SiteId?: number;
    ClientId?: number;
    TopicFields: TopicField[];
  }

  export interface UpdateOrganizationBody {
    SiteId?: number;
    OrganizationId: number;
    ClientId?: number;
    TopicFields: TopicField[];
  }

  export interface CreateOrganization {
    siteId: number;
    organization: CreateOrganizationBody;
  }

  export interface UpdateOrganization {
    siteId: number;
    organization: UpdateOrganizationBody;
  }

  export interface ChangeOrganizationStatus {
    siteId: number;
    organizationId: number;
    statusId: number;
    statusDate?: string;
    statusComments?: string;
  }

  export interface PublishOrganization {
    siteId: number;
    organizationId: number;
    fieldsToInclude?: string[];
    fieldsToExclude?: string[];
  }
}

@Injectable()
export class OrganizationService extends ServiceBase {
  serviceUrl = 'OrganizationService.svc';

  getOrganizationsByView(
    body: OrganizationServiceOptions.GetOrganizationsByView,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<OrganizationServiceModel.OrganizationResult>> {
    return this.origamiPost('/GetOrganizationsByView', body, options, silentErrorCodes, forceRequest);
  }

  getOrganizationViews(
    body: OrganizationServiceOptions.GetOrganizationViews,
  ): Observable<OrigamiResponse<OrganizationServiceModel.OrganizationView[]>> {
    return this.origamiPost('/GetOrganizationsViews', body, null, null, true);
  }

  getOrganization(
    body: OrganizationServiceOptions.GetOrganization,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<OrganizationServiceModel.Organization>> {
    return this.origamiPost('/GetOrganization', body, null, silentErrorCodes, true).pipe(
      map((res) => {
        if (silentErrorCodes?.includes(res?.ErrorCode)) {
          throw new Error(String(res?.ErrorCode));
        }
        return res;
      }),
    );
  }

  getOrganizations(
    body: OrganizationServiceOptions.GetOrganizations,
    force = true,
  ): Observable<OrigamiResponse<OrganizationServiceModel.Organization[]>> {
    return this.origamiPost('/GetOrganizations', body, null, null, force);
  }

  createOrganization(body: OrganizationServiceOptions.CreateOrganization): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreateOrganization', body, null, null, true).pipe(this.dropCacheOnSuccess());
  }

  updateOrganization(body: OrganizationServiceOptions.UpdateOrganization): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/UpdateOrganization', body, null, null, true).pipe(this.dropCacheOnSuccess());
  }

  changeOrganizationStatus(
    body: OrganizationServiceOptions.ChangeOrganizationStatus,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeOrganizationStatus', body, null, null, true).pipe(this.dropCacheOnSuccess());
  }

  deleteOrganization(
    params: { siteId: number; organizationId: number; reason?: string },
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteOrganization', params, null, silentErrorCodes || null, true).pipe(
      this.dropCacheOnSuccess(),
    );
  }

  publishOrganization(body: OrganizationServiceOptions.PublishOrganization): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/PublishOrganization', body, null, null, true);
  }

  private dropCacheOnSuccess<T>(): MonoTypeOperatorFunction<OrigamiResponse<T>> {
    return tap((response) => {
      if (response?.Success) {
        this.dropCacheSection();
      }
    });
  }
}
